import React, { useState, useEffect } from 'react';
import { ArrowRight, Users, Heart, Activity, Brain, Clock, Shield, Zap, TrendingUp, CheckCircle2, Sparkles } from 'lucide-react';
import Navbar from '../components/Navbar';
import Footer from '../components/Footer';

const AboutGewo = () => {
  const [isMobile, setIsMobile] = useState(false);
  const [isSmallMobile, setIsSmallMobile] = useState(false);

  useEffect(() => {
    const checkScreenSize = () => {
      setIsMobile(window.innerWidth <= 768);
      setIsSmallMobile(window.innerWidth <= 480);
    };
    
    checkScreenSize();
    window.addEventListener('resize', checkScreenSize);
    return () => window.removeEventListener('resize', checkScreenSize);
  }, []);
  const focusAreas = [
    {
      icon: '🤰',
      title: 'Women\'s Health',
      color: '#FF6B6B',
      gradient: 'linear-gradient(135deg, #FFE8E8 0%, #FFF0F0 100%)',
      features: [
        'Reproductive health support',
        'Hormonal balance management',
        'Pregnancy & postpartum care',
        'Nutrition & wellness guidance',
        'Menopause support programs'
      ]
    },
    {
      icon: '🩺',
      title: 'Lifestyle Disease Management',
      color: '#28C08A',
      gradient: 'linear-gradient(135deg, #E8F5F1 0%, #F0FAF6 100%)',
      features: [
        'Diabetes management programs',
        'Hypertension monitoring',
        'Obesity & weight management',
        'Heart health optimization',
        'Personalized care plans'
      ]
    },
    {
      icon: '💪',
      title: 'Adult Health',
      color: '#286CFF',
      gradient: 'linear-gradient(135deg, #E3EDFF 0%, #F0F6FF 100%)',
      features: [
        'Preventive health screenings',
        'Work-life balance guidance',
        'General wellness programs',
        'Health risk assessments',
        'Fitness & nutrition counseling'
      ]
    },
    {
      icon: '🧠',
      title: 'Mental Health',
      color: '#9B59B6',
      gradient: 'linear-gradient(135deg, #F3E5FF 0%, #FAF0FF 100%)',
      features: [
        'Therapy & counseling services',
        'Stress & anxiety management',
        'Depression support programs',
        'Mindfulness & meditation guidance',
        'Mental wellness assessments'
      ]
    }
  ];

  const genieCapabilities = [
    {
      icon: Zap,
      title: 'Instant Health Answers',
      description: '24/7 AI-powered responses to health queries and concerns'
    },
    {
      icon: Brain,
      title: 'Smart Triage & Symptom Evaluation',
      description: 'Intelligent assessment to guide appropriate care pathways'
    },
    {
      icon: Sparkles,
      title: 'Personalized Recommendations',
      description: 'Tailored health advice based on individual profiles and history'
    },
    {
      icon: Clock,
      title: 'Alerts & Reminders',
      description: 'Medication reminders, appointment alerts, and daily check-ins'
    },
    {
      icon: TrendingUp,
      title: 'Predictive Risk Assessment',
      description: 'Early detection of health risks through data analysis'
    },
    {
      icon: Activity,
      title: 'Faster Patient-Provider Communication',
      description: 'Seamless information flow between patients and healthcare teams'
    }
  ];

  const ecosystemBenefits = [
    {
      stakeholder: 'For Patients',
      icon: Users,
      color: '#286CFF',
      benefits: [
        '24/7 health guidance and support',
        'Instant answers to health questions',
        'Personalized care recommendations',
        'Easy communication with providers',
        'Medication and appointment reminders'
      ]
    },
    {
      stakeholder: 'For Doctors',
      icon: Heart,
      color: '#28C08A',
      benefits: [
        'Reduced administrative workload',
        'More time for clinical practice',
        'AI-assisted patient triage',
        'Real-time patient data access',
        'Streamlined workflows'
      ]
    },
    {
      stakeholder: 'For Healthcare System',
      icon: Shield,
      color: '#FFD28A',
      benefits: [
        'Improved care coordination',
        'Better resource utilization',
        'Enhanced patient outcomes',
        'Data-driven insights',
        'Cost-effective care delivery'
      ]
    }
  ];

  const whyMatters = [
    { text: 'Early detection of lifestyle diseases through AI monitoring', icon: CheckCircle2 },
    { text: 'Personalized health guidance tailored to individual needs', icon: CheckCircle2 },
    { text: '24/7 patient support through AI and automation', icon: CheckCircle2 },
    { text: 'Doctors freed from administrative workflows', icon: CheckCircle2 },
    { text: 'Faster communication between providers and patients', icon: CheckCircle2 },
    { text: 'Improved independence and quality of life for seniors', icon: CheckCircle2 },
    { text: 'Better health outcomes through connected care systems', icon: CheckCircle2 },
    { text: 'Preventive care reducing long-term healthcare costs', icon: CheckCircle2 }
  ];

  return (
    <div style={styles.page}>
      <Navbar />
      
      {/* Hero Section */}
      <section style={{
        ...styles.heroSection,
        paddingTop: isMobile ? '80px' : '120px',
        paddingBottom: isMobile ? '60px' : '80px'
      }}>
        <div style={styles.container}>
          <div style={{
            ...styles.heroGrid,
            gridTemplateColumns: isMobile ? '1fr' : '1fr 1fr',
            gap: isMobile ? '40px' : '60px'
          }}>
            <div style={styles.heroContent}>
              <div style={{
                ...styles.badge,
                fontSize: isSmallMobile ? '10px' : isMobile ? '11px' : '12px',
                padding: isMobile ? '6px 16px' : '8px 20px'
              }}>ABOUT GEWO HEALTH</div>
              <h1 style={{
                ...styles.heroTitle,
                fontSize: isSmallMobile ? '28px' : isMobile ? '36px' : '52px'
              }}>
                Smarter Care for Geriatric, Adult & Women's Health
              </h1>
              <p style={{
                ...styles.heroSubtitle,
                fontSize: isMobile ? '16px' : '18px',
                marginBottom: isMobile ? '24px' : '32px'
              }}>
                AI-powered Health Genie providing 24/7 support, lifestyle care, and a 
                connected health ecosystem for patients, caregivers, and healthcare providers.
              </p>
              <div style={{
                ...styles.heroCtas,
                flexDirection: isMobile ? 'column' : 'row',
                gap: isMobile ? '12px' : '16px'
              }}>
                <button style={{
                  ...styles.primaryBtn,
                  width: isMobile ? '100%' : 'auto',
                  justifyContent: 'center',
                  padding: isMobile ? '14px 24px' : '16px 32px',
                  fontSize: isMobile ? '15px' : '16px'
                }}>
                  Learn More <ArrowRight size={isMobile ? 18 : 20} style={{ marginLeft: '8px' }} />
                </button>
                <button style={{
                  ...styles.secondaryBtn,
                  width: isMobile ? '100%' : 'auto',
                  justifyContent: 'center',
                  padding: isMobile ? '14px 24px' : '16px 32px',
                  fontSize: isMobile ? '15px' : '16px'
                }}>
                  Request a Demo
                </button>
              </div>
            </div>

            {/* Hero Illustration */}
            <div style={{
              ...styles.heroIllustration,
              order: isMobile ? -1 : 0
            }}>
              <svg viewBox="0 0 500 450" style={{ width: '100%', height: 'auto' }}>
                {/* Background circles */}
                <circle cx="250" cy="225" r="200" fill="#E3EDFF" opacity="0.3"/>
                
                {/* Central Health Genie (AI) */}
                <g transform="translate(250, 200)">
                  <circle cx="0" cy="0" r="60" fill="url(#genieGradient)"/>
                  <defs>
                    <linearGradient id="genieGradient" x1="0%" y1="0%" x2="100%" y2="100%">
                      <stop offset="0%" style={{ stopColor: '#286CFF', stopOpacity: 1 }} />
                      <stop offset="100%" style={{ stopColor: '#5B8BFF', stopOpacity: 1 }} />
                    </linearGradient>
                  </defs>
                  
                  {/* AI Brain/Network */}
                  <circle cx="-15" cy="-10" r="5" fill="white"/>
                  <circle cx="15" cy="-10" r="5" fill="white"/>
                  <circle cx="0" cy="10" r="5" fill="white"/>
                  <circle cx="-20" cy="10" r="4" fill="white" opacity="0.7"/>
                  <circle cx="20" cy="10" r="4" fill="white" opacity="0.7"/>
                  
                  <line x1="-15" y1="-10" x2="15" y2="-10" stroke="white" strokeWidth="2"/>
                  <line x1="-15" y1="-10" x2="0" y2="10" stroke="white" strokeWidth="2"/>
                  <line x1="15" y1="-10" x2="0" y2="10" stroke="white" strokeWidth="2"/>
                  <line x1="-20" y1="10" x2="0" y2="10" stroke="white" strokeWidth="1.5"/>
                  <line x1="20" y1="10" x2="0" y2="10" stroke="white" strokeWidth="1.5"/>
                  
                  {/* Sparkle effects */}
                  <g transform="translate(40, -30)">
                    <path d="M0 -8 L2 -2 L8 0 L2 2 L0 8 L-2 2 L-8 0 L-2 -2 Z" fill="#FFD28A"/>
                  </g>
                  <g transform="translate(-45, -20)">
                    <path d="M0 -6 L1.5 -1.5 L6 0 L1.5 1.5 L0 6 L-1.5 1.5 L-6 0 L-1.5 -1.5 Z" fill="#FFD28A"/>
                  </g>
                </g>
                
                {/* Patient (Left) */}
                <g transform="translate(100, 200)">
                  <circle cx="0" cy="0" r="35" fill="white" opacity="0.9"/>
                  <circle cx="0" cy="-5" r="15" fill="#FFD28A"/>
                  <rect x="-12" y="10" width="24" height="25" rx="4" fill="#286CFF"/>
                  <text x="0" y="50" textAnchor="middle" fontSize="11" fill="#64748B" fontWeight="600">Patient</text>
                  
                  {/* Connection line */}
                  <line x1="35" y1="0" x2="115" y2="0" stroke="#286CFF" strokeWidth="2" strokeDasharray="5,5" opacity="0.5"/>
                </g>
                
                {/* Doctor (Right) */}
                <g transform="translate(400, 200)">
                  <circle cx="0" cy="0" r="35" fill="white" opacity="0.9"/>
                  <circle cx="0" cy="-5" r="15" fill="#FBBF82"/>
                  <rect x="-12" y="10" width="24" height="25" rx="4" fill="white"/>
                  <path d="M-6 10 L0 15 L6 10" fill="#286CFF"/>
                  <text x="0" y="50" textAnchor="middle" fontSize="11" fill="#64748B" fontWeight="600">Doctor</text>
                  
                  {/* Connection line */}
                  <line x1="-35" y1="0" x2="-115" y2="0" stroke="#28C08A" strokeWidth="2" strokeDasharray="5,5" opacity="0.5"/>
                </g>
                
                {/* Caregiver (Bottom Left) */}
                <g transform="translate(130, 340)">
                  <circle cx="0" cy="0" r="30" fill="white" opacity="0.9"/>
                  <circle cx="0" cy="-5" r="12" fill="#FF6B6B"/>
                  <rect x="-10" y="7" width="20" height="20" rx="3" fill="#28C08A"/>
                  <text x="0" y="40" textAnchor="middle" fontSize="10" fill="#64748B" fontWeight="600">Caregiver</text>
                  
                  {/* Connection line */}
                  <line x1="30" y1="-30" x2="90" y2="-100" stroke="#FF6B6B" strokeWidth="2" strokeDasharray="5,5" opacity="0.5"/>
                </g>
                
                {/* Lab/Pharmacy (Bottom Right) */}
                <g transform="translate(370, 340)">
                  <circle cx="0" cy="0" r="30" fill="white" opacity="0.9"/>
                  <rect x="-10" y="-10" width="20" height="20" rx="2" fill="#286CFF" opacity="0.3"/>
                  <path d="M-5 -2 L0 -7 L5 -2 L3 -2 L3 5 L-3 5 L-3 -2 Z" fill="#286CFF"/>
                  <text x="0" y="40" textAnchor="middle" fontSize="10" fill="#64748B" fontWeight="600">Lab/Rx</text>
                  
                  {/* Connection line */}
                  <line x1="-30" y1="-30" x2="-90" y2="-100" stroke="#FFD28A" strokeWidth="2" strokeDasharray="5,5" opacity="0.5"/>
                </g>
                
                {/* Data flow indicators */}
                <g opacity="0.7">
                  <circle cx="180" cy="200" r="4" fill="#286CFF">
                    <animate attributeName="cx" values="180;220;180" dur="3s" repeatCount="indefinite"/>
                  </circle>
                  <circle cx="320" cy="200" r="4" fill="#28C08A">
                    <animate attributeName="cx" values="320;280;320" dur="3s" repeatCount="indefinite"/>
                  </circle>
                </g>
              </svg>
            </div>
          </div>
        </div>
      </section>

      {/* About GEWO Health */}
      <section style={{
        ...styles.aboutSection,
        padding: isMobile ? '60px 0' : '80px 0'
      }}>
        <div style={styles.container}>
          <div style={styles.aboutContent}>
            <h2 style={{
              ...styles.sectionTitle,
              fontSize: isSmallMobile ? '24px' : isMobile ? '32px' : '40px'
            }}>What is GEWO Health?</h2>
            <div style={styles.aboutText}>
              <p style={{
                ...styles.paragraph,
                fontSize: isMobile ? '15px' : '17px'
              }}>
                <strong>GEWO Health</strong> is a pioneering healthcare initiative designed to address the most important 
                healthcare needs across all stages of life—<strong>Geriatric Health, Adult Health, Women's Wellness, 
                and Lifestyle Disease Management</strong>. Our platform integrates preventive care, early diagnosis, 
                continuous monitoring, and intelligent automation to create a comprehensive healthcare ecosystem.
              </p>
              <p style={{
                ...styles.paragraph,
                fontSize: isMobile ? '15px' : '17px'
              }}>
                At the heart of GEWO Health is the <strong>Health Genie</strong>—our AI-powered companion that makes 
                health assistance available to patients 24/7 while enabling doctors to focus more on their clinical 
                practice rather than administrative tasks.
              </p>
              <p style={{
                ...styles.paragraph,
                fontSize: isMobile ? '15px' : '17px'
              }}>
                Through advanced software, technology, and Artificial Intelligence, GEWO Health creates a seamlessly 
                connected environment where patients receive immediate guidance, caregivers stay informed, and healthcare 
                providers deliver more efficient, personalized care.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Key Focus Areas */}
      <section style={{
        ...styles.focusSection,
        padding: isMobile ? '60px 0' : '80px 0'
      }}>
        <div style={styles.container}>
          <h2 style={{
            ...styles.sectionTitle,
            fontSize: isSmallMobile ? '24px' : isMobile ? '32px' : '40px'
          }}>Our Key Focus Areas</h2>
          <p style={{
            ...styles.sectionSubtitle,
            fontSize: isMobile ? '16px' : '18px',
            marginBottom: isMobile ? '40px' : '60px'
          }}>
            Comprehensive healthcare solutions tailored to life's different stages and needs
          </p>
          
          <div style={{
            ...styles.focusGrid,
            gridTemplateColumns: isMobile ? '1fr' : 'repeat(2, 1fr)',
            gap: isMobile ? '24px' : '32px'
          }}>
            {focusAreas.map((area, index) => (
              <div key={index} style={{
                ...styles.focusCard,
                background: area.gradient,
                padding: isMobile ? '32px 24px' : '40px'
              }}>
                <div style={{
                  ...styles.focusEmoji,
                  fontSize: isMobile ? '48px' : '56px'
                }}>{area.icon}</div>
                <h3 style={{
                  ...styles.focusTitle,
                  fontSize: isMobile ? '20px' : '24px'
                }}>{area.title}</h3>
                <ul style={styles.focusFeatures}>
                  {area.features.map((feature, idx) => (
                    <li key={idx} style={{
                      ...styles.focusFeature,
                      fontSize: isMobile ? '14px' : '15px'
                    }}>
                      <CheckCircle2 size={isMobile ? 14 : 16} color={area.color} style={{ marginRight: '8px', flexShrink: 0 }} />
                      <span>{feature}</span>
                    </li>
                  ))}
                </ul>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Health Genie Section */}
      <section style={{
        ...styles.genieSection,
        padding: isMobile ? '60px 0' : '80px 0'
      }}>
        <div style={styles.container}>
          <div style={{
            ...styles.genieHeader,
            flexDirection: isMobile ? 'column' : 'row',
            gap: isMobile ? '20px' : '24px'
          }}>
            <div style={styles.genieIcon}>
              <svg viewBox="0 0 100 100" style={{ width: isMobile ? '64px' : '80px', height: isMobile ? '64px' : '80px' }}>
                <defs>
                  <linearGradient id="genieGrad" x1="0%" y1="0%" x2="100%" y2="100%">
                    <stop offset="0%" style={{ stopColor: '#286CFF', stopOpacity: 1 }} />
                    <stop offset="100%" style={{ stopColor: '#5B8BFF', stopOpacity: 1 }} />
                  </linearGradient>
                </defs>
                <circle cx="50" cy="50" r="45" fill="url(#genieGrad)"/>
                
                {/* AI Network */}
                <g transform="translate(50, 50)">
                  <circle cx="-15" cy="-10" r="5" fill="white"/>
                  <circle cx="15" cy="-10" r="5" fill="white"/>
                  <circle cx="0" cy="10" r="5" fill="white"/>
                  <circle cx="-20" cy="10" r="4" fill="white" opacity="0.7"/>
                  <circle cx="20" cy="10" r="4" fill="white" opacity="0.7"/>
                  
                  <line x1="-15" y1="-10" x2="15" y2="-10" stroke="white" strokeWidth="2"/>
                  <line x1="-15" y1="-10" x2="0" y2="10" stroke="white" strokeWidth="2"/>
                  <line x1="15" y1="-10" x2="0" y2="10" stroke="white" strokeWidth="2"/>
                  <line x1="-20" y1="10" x2="0" y2="10" stroke="white" strokeWidth="1.5"/>
                  <line x1="20" y1="10" x2="0" y2="10" stroke="white" strokeWidth="1.5"/>
                </g>
                
                {/* Sparkles */}
                <path d="M25 15 L27 20 L32 22 L27 24 L25 29 L23 24 L18 22 L23 20 Z" fill="#FFD28A"/>
                <path d="M75 25 L77 30 L82 32 L77 34 L75 39 L73 34 L68 32 L73 30 Z" fill="#FFD28A"/>
              </svg>
            </div>
            <div style={{ textAlign: isMobile ? 'center' : 'left' }}>
              <h2 style={{
                ...styles.genieTitle,
                fontSize: isSmallMobile ? '24px' : isMobile ? '32px' : '40px'
              }}>Introducing the Health Genie</h2>
              <p style={{
                ...styles.genieSubtitle,
                fontSize: isMobile ? '16px' : '20px'
              }}>
                Your 24/7 AI-Powered Healthcare Companion
              </p>
            </div>
          </div>

          <div style={styles.genieDescription}>
            <p style={{
              ...styles.paragraph,
              fontSize: isMobile ? '15px' : '17px'
            }}>
              The <strong>Health Genie</strong> uses advanced Artificial Intelligence to revolutionize healthcare delivery. 
              Working around the clock—24/7—it ensures patients always receive immediate guidance while doctors benefit 
              from significantly reduced administrative workload. This AI companion creates a smarter, more efficient 
              healthcare ecosystem where faster answers lead to better productivity and seamless interactions.
            </p>
          </div>

          <div style={{
            ...styles.capabilitiesGrid,
            gridTemplateColumns: isMobile ? '1fr' : 'repeat(3, 1fr)',
            gap: isMobile ? '20px' : '24px'
          }}>
            {genieCapabilities.map((capability, index) => {
              const Icon = capability.icon;
              return (
                <div key={index} style={{
                  ...styles.capabilityCard,
                  padding: isMobile ? '24px 20px' : '32px 24px'
                }}>
                  <div style={{
                    ...styles.capabilityIcon,
                    width: isMobile ? '56px' : '64px',
                    height: isMobile ? '56px' : '64px'
                  }}>
                    <Icon size={isMobile ? 24 : 28} color="#286CFF" />
                  </div>
                  <h3 style={{
                    ...styles.capabilityTitle,
                    fontSize: isMobile ? '15px' : '16px'
                  }}>{capability.title}</h3>
                  <p style={{
                    ...styles.capabilityDescription,
                    fontSize: isMobile ? '12px' : '13px'
                  }}>{capability.description}</p>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* Ecosystem Section */}
      <section style={{
        ...styles.ecosystemSection,
        padding: isMobile ? '60px 0' : '80px 0'
      }}>
        <div style={styles.container}>
          <h2 style={{
            ...styles.sectionTitle,
            fontSize: isSmallMobile ? '24px' : isMobile ? '32px' : '40px'
          }}>Connected Healthcare Ecosystem</h2>
          <p style={{
            ...styles.sectionSubtitle,
            fontSize: isMobile ? '16px' : '18px',
            marginBottom: isMobile ? '40px' : '60px'
          }}>
            Bringing together patients, providers, and technology for better outcomes
          </p>

          {/* Visual Ecosystem */}
          <div style={{
            ...styles.ecosystemVisual,
            marginBottom: isMobile ? '40px' : '60px'
          }}>
            <svg viewBox="0 0 800 480" style={{ width: '100%', maxWidth: '800px', height: 'auto', margin: '0 auto', display: 'block' }}>
              {/* Central Hub - Health Genie */}
              <g transform="translate(400, 200)">
                <circle cx="0" cy="0" r="60" fill="#286CFF"/>
                <text x="0" y="-70" textAnchor="middle" fontSize="16" fontWeight="700" fill="#2D3748">Health Genie</text>
                <text x="0" y="-50" textAnchor="middle" fontSize="12" fill="#64748B">AI Hub</text>
                <text x="0" y="5" textAnchor="middle" fontSize="14" fontWeight="600" fill="white">AI</text>
                <text x="0" y="20" textAnchor="middle" fontSize="10" fill="white">24/7</text>
              </g>
              
              {/* Patients - Top Left */}
              <g transform="translate(200, 80)">
                <circle cx="0" cy="0" r="45" fill="#79A8FF"/>
                <text x="0" y="5" textAnchor="middle" fontSize="24">👥</text>
                <text x="0" y="60" textAnchor="middle" fontSize="14" fontWeight="600" fill="#2D3748">Patients</text>
                <line x1="45" y1="40" x2="155" y2="140" stroke="#286CFF" strokeWidth="3"/>
              </g>
              
              {/* Doctors - Top Right */}
              <g transform="translate(600, 80)">
                <circle cx="0" cy="0" r="45" fill="#28C08A"/>
                <text x="0" y="5" textAnchor="middle" fontSize="24">👨‍⚕️</text>
                <text x="0" y="60" textAnchor="middle" fontSize="14" fontWeight="600" fill="#2D3748">Doctors</text>
                <line x1="-45" y1="40" x2="-155" y2="140" stroke="#28C08A" strokeWidth="3"/>
              </g>
              
              {/* Caregivers - Bottom Left */}
              <g transform="translate(200, 320)">
                <circle cx="0" cy="0" r="45" fill="#FF6B6B"/>
                <text x="0" y="5" textAnchor="middle" fontSize="24">🤝</text>
                <text x="0" y="65" textAnchor="middle" fontSize="14" fontWeight="600" fill="#2D3748">Caregivers</text>
                <line x1="45" y1="-40" x2="155" y2="-60" stroke="#FF6B6B" strokeWidth="3"/>
              </g>
              
              {/* Labs/Pharmacy - Bottom Right */}
              <g transform="translate(600, 320)">
                <circle cx="0" cy="0" r="45" fill="#FFD28A"/>
                <text x="0" y="5" textAnchor="middle" fontSize="24">🏥</text>
                <text x="0" y="65" textAnchor="middle" fontSize="14" fontWeight="600" fill="#2D3748">Labs & Rx</text>
                <line x1="-45" y1="-40" x2="-155" y2="-60" stroke="#FFD28A" strokeWidth="3"/>
              </g>
              
              {/* Benefits labels - with better spacing */}
              <g transform="translate(400, 430)">
                <text x="0" y="0" textAnchor="middle" fontSize="13" fill="#64748B" fontWeight="600">
                  Improved Productivity • Enhanced Connectivity • Faster Response • Better Accuracy
                </text>
              </g>
            </svg>
          </div>

          {/* Ecosystem Benefits */}
          <div style={{
            ...styles.ecosystemBenefits,
            gridTemplateColumns: isMobile ? '1fr' : 'repeat(3, 1fr)',
            gap: isMobile ? '24px' : '32px'
          }}>
            {ecosystemBenefits.map((item, index) => {
              const Icon = item.icon;
              return (
                <div key={index} style={{
                  ...styles.ecosystemCard,
                  padding: isMobile ? '24px 20px' : '32px 24px'
                }}>
                  <div style={{
                    ...styles.ecosystemCardIcon,
                    background: `${item.color}20`,
                    width: isMobile ? '56px' : '64px',
                    height: isMobile ? '56px' : '64px'
                  }}>
                    <Icon size={isMobile ? 28 : 32} color={item.color} />
                  </div>
                  <h3 style={{
                    ...styles.ecosystemCardTitle,
                    fontSize: isMobile ? '18px' : '20px'
                  }}>{item.stakeholder}</h3>
                  <ul style={styles.ecosystemList}>
                    {item.benefits.map((benefit, idx) => (
                      <li key={idx} style={{
                        ...styles.ecosystemListItem,
                        fontSize: isMobile ? '13px' : '14px'
                      }}>
                        <CheckCircle2 size={isMobile ? 14 : 16} color={item.color} style={{ marginRight: '8px', flexShrink: 0 }} />
                        <span>{benefit}</span>
                      </li>
                    ))}
                  </ul>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* Why GEWO Matters */}
      <section style={{
        ...styles.whySection,
        padding: isMobile ? '60px 0' : '80px 0'
      }}>
        <div style={styles.container}>
          <h2 style={{
            ...styles.sectionTitle,
            fontSize: isSmallMobile ? '24px' : isMobile ? '32px' : '40px'
          }}>Why GEWO Health Matters</h2>
          <p style={{
            ...styles.sectionSubtitle,
            fontSize: isMobile ? '16px' : '18px',
            marginBottom: isMobile ? '40px' : '60px'
          }}>
            Transforming healthcare through technology, intelligence, and connection
          </p>
          
          <div style={{
            ...styles.whyGrid,
            gridTemplateColumns: isMobile ? '1fr' : 'repeat(2, 1fr)',
            gap: isMobile ? '16px' : '20px'
          }}>
            {whyMatters.map((item, index) => {
              const Icon = item.icon;
              return (
                <div key={index} style={{
                  ...styles.whyCard,
                  padding: isMobile ? '16px 20px' : '20px 24px'
                }}>
                  <Icon size={isMobile ? 20 : 24} color="#28C08A" style={{ flexShrink: 0 }} />
                  <span style={{
                    ...styles.whyText,
                    fontSize: isMobile ? '14px' : '15px'
                  }}>{item.text}</span>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section style={{
        ...styles.ctaSection,
        padding: isMobile ? '60px 0' : '80px 0'
      }}>
        <div style={styles.container}>
          <div style={styles.ctaContent}>
            <h2 style={{
              ...styles.ctaTitle,
              fontSize: isSmallMobile ? '24px' : isMobile ? '32px' : '40px'
            }}>Join the GEWO Health Ecosystem</h2>
            <p style={{
              ...styles.ctaSubtitle,
              fontSize: isMobile ? '16px' : '20px',
              marginBottom: isMobile ? '32px' : '40px'
            }}>
              Smarter Care Starts Here – Experience the future of connected healthcare
            </p>
            <div style={{
              ...styles.ctaButtons,
              flexDirection: isMobile ? 'column' : 'row',
              gap: isMobile ? '12px' : '16px'
            }}>
              <button style={{
                ...styles.ctaButton,
                width: isMobile ? '100%' : 'auto',
                justifyContent: 'center',
                padding: isMobile ? '14px 24px' : '16px 32px',
                fontSize: isMobile ? '15px' : '16px'
              }}>
                Learn More <ArrowRight size={isMobile ? 18 : 20} style={{ marginLeft: '8px' }} />
              </button>
              <button style={{
                ...styles.ctaButtonSecondary,
                width: isMobile ? '100%' : 'auto',
                justifyContent: 'center',
                padding: isMobile ? '14px 24px' : '16px 32px',
                fontSize: isMobile ? '15px' : '16px'
              }}>
                Request a Demo
              </button>
              <button style={{
                ...styles.ctaButtonOutline,
                width: isMobile ? '100%' : 'auto',
                justifyContent: 'center',
                padding: isMobile ? '14px 24px' : '16px 32px',
                fontSize: isMobile ? '15px' : '16px'
              }}>
                Partner with Us
              </button>
            </div>
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
};

const styles = {
  page: {
    minHeight: '100vh',
    background: '#FFFFFF'
  },
  heroSection: {
    background: 'linear-gradient(135deg, #286CFF 0%, #5B8BFF 100%)',
    paddingTop: '120px',
    paddingBottom: '80px',
    color: 'white'
  },
  container: {
    maxWidth: '1200px',
    margin: '0 auto',
    padding: '0 24px'
  },
  heroGrid: {
    display: 'grid',
    gridTemplateColumns: '1fr 1fr',
    gap: '60px',
    alignItems: 'center'
  },
  heroContent: {},
  badge: {
    display: 'inline-block',
    background: 'rgba(255, 255, 255, 0.2)',
    padding: '8px 20px',
    borderRadius: '20px',
    fontSize: '12px',
    fontWeight: '700',
    letterSpacing: '0.5px',
    marginBottom: '24px'
  },
  heroTitle: {
    fontSize: '52px',
    fontWeight: '700',
    lineHeight: '1.2',
    marginBottom: '20px'
  },
  heroSubtitle: {
    fontSize: '18px',
    lineHeight: '1.6',
    marginBottom: '32px',
    opacity: 0.95
  },
  heroCtas: {
    display: 'flex',
    gap: '16px'
  },
  primaryBtn: {
    background: 'white',
    color: '#286CFF',
    border: 'none',
    borderRadius: '12px',
    padding: '16px 32px',
    fontSize: '16px',
    fontWeight: '600',
    cursor: 'pointer',
    display: 'inline-flex',
    alignItems: 'center',
    transition: 'all 0.2s ease',
    boxShadow: '0 8px 24px rgba(0, 0, 0, 0.15)'
  },
  secondaryBtn: {
    background: 'transparent',
    color: 'white',
    border: '2px solid white',
    borderRadius: '12px',
    padding: '16px 32px',
    fontSize: '16px',
    fontWeight: '600',
    cursor: 'pointer',
    transition: 'all 0.2s ease'
  },
  heroIllustration: {
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center'
  },
  aboutSection: {
    padding: '80px 0',
    background: '#F8FAFC'
  },
  aboutContent: {
    maxWidth: '900px',
    margin: '0 auto'
  },
  sectionTitle: {
    fontSize: '40px',
    fontWeight: '700',
    color: '#2D3748',
    textAlign: 'center',
    marginBottom: '16px'
  },
  aboutText: {
    marginTop: '40px'
  },
  paragraph: {
    fontSize: '17px',
    color: '#2D3748',
    lineHeight: '1.8',
    marginBottom: '24px'
  },
  focusSection: {
    padding: '80px 0',
    background: '#FFFFFF'
  },
  sectionSubtitle: {
    fontSize: '18px',
    color: '#64748B',
    textAlign: 'center',
    marginBottom: '60px',
    maxWidth: '700px',
    margin: '0 auto 60px'
  },
  focusGrid: {
    display: 'grid',
    gridTemplateColumns: 'repeat(2, 1fr)',
    gap: '32px'
  },
  focusCard: {
    background: 'white',
    borderRadius: '24px',
    padding: '40px',
    boxShadow: '0 4px 16px rgba(0, 0, 0, 0.06)'
  },
  focusEmoji: {
    fontSize: '56px',
    marginBottom: '20px'
  },
  focusTitle: {
    fontSize: '24px',
    fontWeight: '700',
    color: '#2D3748',
    marginBottom: '20px'
  },
  focusFeatures: {
    listStyle: 'none',
    padding: 0,
    margin: 0,
    display: 'flex',
    flexDirection: 'column',
    gap: '12px'
  },
  focusFeature: {
    display: 'flex',
    alignItems: 'flex-start',
    fontSize: '15px',
    color: '#2D3748',
    lineHeight: '1.5'
  },
  genieSection: {
    padding: '80px 0',
    background: 'linear-gradient(180deg, #F8FAFC 0%, #E3EDFF 100%)'
  },
  genieHeader: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    gap: '24px',
    marginBottom: '32px'
  },
  genieIcon: {},
  genieTitle: {
    fontSize: '40px',
    fontWeight: '700',
    color: '#2D3748',
    marginBottom: '8px'
  },
  genieSubtitle: {
    fontSize: '20px',
    color: '#286CFF',
    fontWeight: '600'
  },
  genieDescription: {
    maxWidth: '900px',
    margin: '0 auto 60px'
  },
  capabilitiesGrid: {
    display: 'grid',
    gridTemplateColumns: 'repeat(3, 1fr)',
    gap: '24px'
  },
  capabilityCard: {
    background: 'white',
    borderRadius: '16px',
    padding: '32px 24px',
    textAlign: 'center',
    boxShadow: '0 4px 16px rgba(0, 0, 0, 0.06)'
  },
  capabilityIcon: {
    width: '64px',
    height: '64px',
    background: '#E3EDFF',
    borderRadius: '16px',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    margin: '0 auto 16px'
  },
  capabilityTitle: {
    fontSize: '16px',
    fontWeight: '700',
    color: '#2D3748',
    marginBottom: '8px'
  },
  capabilityDescription: {
    fontSize: '13px',
    color: '#64748B',
    lineHeight: '1.5'
  },
  ecosystemSection: {
    padding: '80px 0',
    background: '#FFFFFF'
  },
  ecosystemVisual: {
    marginBottom: '60px'
  },
  ecosystemBenefits: {
    display: 'grid',
    gridTemplateColumns: 'repeat(3, 1fr)',
    gap: '32px'
  },
  ecosystemCard: {
    background: '#F8FAFC',
    borderRadius: '20px',
    padding: '32px 24px'
  },
  ecosystemCardIcon: {
    width: '64px',
    height: '64px',
    borderRadius: '16px',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    marginBottom: '20px'
  },
  ecosystemCardTitle: {
    fontSize: '20px',
    fontWeight: '700',
    color: '#2D3748',
    marginBottom: '16px'
  },
  ecosystemList: {
    listStyle: 'none',
    padding: 0,
    margin: 0,
    display: 'flex',
    flexDirection: 'column',
    gap: '10px'
  },
  ecosystemListItem: {
    display: 'flex',
    alignItems: 'flex-start',
    fontSize: '14px',
    color: '#2D3748',
    lineHeight: '1.5'
  },
  whySection: {
    padding: '80px 0',
    background: '#F8FAFC'
  },
  whyGrid: {
    display: 'grid',
    gridTemplateColumns: 'repeat(2, 1fr)',
    gap: '20px',
    maxWidth: '900px',
    margin: '0 auto'
  },
  whyCard: {
    background: 'white',
    borderRadius: '12px',
    padding: '20px 24px',
    display: 'flex',
    alignItems: 'center',
    gap: '16px',
    boxShadow: '0 2px 8px rgba(0, 0, 0, 0.04)'
  },
  whyText: {
    fontSize: '15px',
    color: '#2D3748',
    lineHeight: '1.5'
  },
  ctaSection: {
    padding: '80px 0',
    background: 'linear-gradient(135deg, #286CFF 0%, #5B8BFF 100%)',
    color: 'white'
  },
  ctaContent: {
    textAlign: 'center'
  },
  ctaTitle: {
    fontSize: '40px',
    fontWeight: '700',
    marginBottom: '16px',
    color: 'white'
  },
  ctaSubtitle: {
    fontSize: '20px',
    marginBottom: '40px',
    opacity: 0.95
  },
  ctaButtons: {
    display: 'flex',
    gap: '16px',
    justifyContent: 'center'
  },
  ctaButton: {
    background: 'white',
    color: '#286CFF',
    border: 'none',
    borderRadius: '12px',
    padding: '16px 32px',
    fontSize: '16px',
    fontWeight: '600',
    cursor: 'pointer',
    display: 'inline-flex',
    alignItems: 'center',
    transition: 'all 0.2s ease',
    boxShadow: '0 8px 24px rgba(0, 0, 0, 0.15)'
  },
  ctaButtonSecondary: {
    background: 'rgba(255, 255, 255, 0.2)',
    color: 'white',
    border: 'none',
    borderRadius: '12px',
    padding: '16px 32px',
    fontSize: '16px',
    fontWeight: '600',
    cursor: 'pointer',
    transition: 'all 0.2s ease'
  },
  ctaButtonOutline: {
    background: 'transparent',
    color: 'white',
    border: '2px solid white',
    borderRadius: '12px',
    padding: '16px 32px',
    fontSize: '16px',
    fontWeight: '600',
    cursor: 'pointer',
    transition: 'all 0.2s ease'
  }
};

export default AboutGewo;
